'use client'

import * as React from 'react'
import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { clsx } from 'clsx'
import { CheckIcon } from '@radix-ui/react-icons'

import { Label, LabelProps } from './input'

type CheckboxProps = React.ComponentPropsWithoutRef<
  typeof CheckboxPrimitive.Root
> & {
  clickTargetPadding?: number
}

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, clickTargetPadding, ...props }, ref) => {
  const itemSize = 14
  const containerSize = itemSize + (clickTargetPadding ?? 0) * 2

  return (
    <CheckboxPrimitive.Root
      ref={ref}
      className={clsx(
        'radix-state-checked:bg-dark-control-base/15 group/checkbox group relative inline-flex shrink-0 cursor-default items-center justify-center rounded-lg -outline-offset-4 focus-visible:outline focus-visible:outline-dark-control-base focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      {...props}
      style={{
        padding: clickTargetPadding ?? 0,
        width: containerSize,
        height: containerSize,
      }}
    >
      <div>
        <span
          className={clsx(
            'absolute rounded border border-dark-elevated bg-dark-shade-hover group-enabled/checkbox:group-hover/checkbox:border-dark-faint group-radix-state-checked/checkbox:!border-dark-control-base group-radix-state-checked:text-white group-radix-state-checked:bg-dark-control-base',
            'outline-1 outline-offset-2 outline-dark-control-base group-focus-visible/checkbox:outline'
          )}
          style={{ inset: clickTargetPadding ?? 0 }}
        >
          <CheckboxPrimitive.Indicator
            className={clsx('flex items-center justify-center')}
          >
            <CheckIcon width={13} height={13} />
          </CheckboxPrimitive.Indicator>
        </span>
      </div>
    </CheckboxPrimitive.Root>
  )
})

Checkbox.displayName = CheckboxPrimitive.Root.displayName

type LabeledCheckboxProps = LabelProps & {
  name: string
  checkboxProps?: Omit<CheckboxProps, 'name'>
  actions?: Array<React.ReactNode>
}

const LabeledCheckbox = React.forwardRef<
  HTMLLabelElement,
  LabeledCheckboxProps
>(
  (
    {
      label,
      name,
      horizontal = true,
      untitled,
      blocked = false,
      checkboxProps,
      actions,
      ...rest
    },
    ref
  ) => {
    const reactId = React.useId()
    return (
      <Label
        ref={ref}
        htmlFor={checkboxProps?.id ?? reactId}
        draggable={blocked === true}
        blocked={blocked}
        label={label}
        horizontal={horizontal}
        untitled={untitled}
        {...rest}
      >
        {actions}
        <Checkbox
          id={checkboxProps?.id ?? reactId}
          name={name}
          {...checkboxProps}
        />
      </Label>
    )
  }
)

export { Checkbox, LabeledCheckbox }
